// Section 9: House (stone). 14 build points, 6 wood and 10 stone; 5 beds; warmth need 0.7x for
// its residents (scaled by condition, D-13); fire risk 0.3x: the settlement's fire chance is
// scaled by the mean fire weight of its buildings (houses 0.3, others 1), and the events stream
// weights which building burns by `fireWeight` (core/queries.ts).
// Constants: config.buildings.house.
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import { conditionFactor, fireWeight, hasBuilding } from '../../core/queries';
import { sortedValues } from '../../core/sorted';
import { buildingEffectHook, combineModifiers, noDeltas } from '../../core/content-helpers';
import { defineBuilding } from '../../core/registry';

export function house(config: Config) {
  const c = config.buildings.house;

  return defineBuilding({
    id: 'house',
    label: 'House',
    section: 'S9',
    buildPoints: c.buildPoints,
    materials: { wood: c.wood, stone: c.stone },
    material: 'stone',
    beds: c.beds,
    plots: 0,
    capacity: {},
    limit: { kind: 'none' },
    modifierTargets: ['demand', 'eventWeight', 'buildingEffect'],
    modifierKeys: { demand: ['warmth'] },
    modifiers: combineModifiers(buildingEffectHook('house'), (ctx, target) => {
      if (target.kind === 'demand' && target.resource === 'warmth') {
        const p = ctx.state.people[target.personId];
        const home = p === undefined ? null : ctx.state.households[p.householdId]?.buildingId;
        const b = home === null || home === undefined ? undefined : ctx.state.buildings[home];
        if (b?.type !== 'house') return [];
        const e = conditionFactor(ctx.cfg, b);
        return e > 0
          ? [mod.mul('building:house', 1 - (1 - c.warmthFactor) * e, 'stone house')]
          : [];
      }
      if (target.kind === 'eventWeight' && target.eventId === 'fire') {
        if (!hasBuilding(ctx.state, 'house')) return [];
        const all = sortedValues(ctx.state.buildings);
        const mean = all.reduce((n, b) => n + fireWeight(ctx.cfg, b), 0) / all.length;
        return [mod.mul('building:house', mean, 'stone houses')];
      }
      return [];
    }),
    onComplete: noDeltas,
    onDamaged: noDeltas,
  });
}
